import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { ZodTypeAny } from 'zod';

export type ValidatorSchema<T> = {
    [K in keyof T]?: {
        required?: boolean | ((values: T) => boolean);
        validate?: (value: T[K], values?: T) => string | null;
    };
};

/* ─── Helper: is this a Zod schema? ─── */
function isZodSchema(schema: any): schema is ZodTypeAny {
    return schema != null && typeof schema === 'object' && typeof schema.safeParse === 'function';
}

/* ─── Shared validators (still exported for use outside the hook) ─── */
export const validators = {
    isOnlyLetters: (field = 'Field') => (val: string) =>
        /^[A-Za-z\s]+$/.test(val) ? null : `${field} must contain only letters and spaces.`,

    isValidEmail: (val: string) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val) ? null : 'Please enter a valid email address.',

    isValidPhone: (val: string) =>
        /^\d{10}$/.test(val) ? null : 'Phone number must be exactly 10 digits.',

    isMinLength: (min: number, field = 'Field') => (val: string) =>
        val.length >= min ? null : `${field} must be at least ${min} characters.`,

    isMaxLength: (max: number, field = 'Field') => (val: string) =>
        val.length <= max ? null : `${field} must be less than ${max + 1} characters.`,

    matchesRegex: (regex: RegExp, message: string) => (val: string) =>
        regex.test(val) ? null : message,

    isNotEmpty: (field = 'Field') => (val: string) =>
        val.trim() !== '' ? null : `${field} is required.`,

    isAlphaNumeric: (field = 'Field') => (val: string) =>
        /^[a-zA-Z0-9]+$/.test(val) ? null : `${field} must be alphanumeric.`,
};

export function useFormValidator<T extends Record<string, any>>(
    initialValues: T,
    schema: ZodTypeAny | ValidatorSchema<T>
) {
    const [formData, setFormData] = useState<T>(initialValues);
    const [errors, setErrors] = useState<Record<keyof T, string>>({} as Record<keyof T, string>);
    // Stores the last Zod-parsed (transformed) data synchronously so handleSubmit
    // can use it immediately without waiting for async setFormData to flush.
    const lastParsedRef = React.useRef<T | null>(null);

    const _isZod = isZodSchema(schema);

    const validateField = (field: keyof T, value: any): string => {
        if (_isZod) {
            const partial = { ...formData, [field]: value };
            const result = (schema as ZodTypeAny).safeParse(partial);
            if (!result.success) {
                const fieldErrors = result.error.flatten().fieldErrors;
                const fieldName = String(field);
                const msgs = (fieldErrors as any)[fieldName];
                return msgs?.[0] ?? '';
            }
            return '';
        }

        // Legacy path
        const rules = (schema as ValidatorSchema<T>)[field];
        const readableField = String(field)
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (char) => char.toUpperCase());

        if (!rules) return '';

        const isRequired =
            typeof rules.required === 'function' ? rules.required(formData) : rules.required;

        if (isRequired && (!value || (typeof value === 'string' && !value.trim()))) {
            return `${readableField} is required.`;
        }

        if (rules?.validate && value) {
            const error = rules.validate(value, formData);
            if (error) return error;
        }

        return '';
    };

    const handleChange = (field: keyof T, value: any) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        const fieldError = validateField(field, value);
        setErrors((prev) => ({ ...prev, [field]: fieldError }));
    };

    const validate = (): boolean => {
        if (_isZod) {
            const result = (schema as ZodTypeAny).safeParse(formData);

            if (!result.success) {
                const fieldErrors = result.error.flatten().fieldErrors as Record<string, string[]>;
                const newErrors: Record<keyof T, string> = {} as Record<keyof T, string>;

                for (const [key, msgs] of Object.entries(fieldErrors)) {
                    if (msgs && msgs[0]) {
                        (newErrors as any)[key] = msgs[0];
                    }
                }

                setErrors(newErrors);

                // Toast the first error
                const firstMsg = Object.values(newErrors)[0] as string;
                if (firstMsg) toast.error(firstMsg);

                return false;
            }

            // Store transformed result synchronously (React state is async)
            lastParsedRef.current = result.data as T;
            // Also update state for the next render
            if (result.data && typeof result.data === 'object') {
                setFormData((prev) => ({ ...prev, ...(result.data as Partial<T>) }));
            }

            setErrors({} as Record<keyof T, string>);
            return true;
        }

        // Legacy path (unchanged)
        const newErrors = {} as Record<keyof T, string>;
        (Object.entries(schema as ValidatorSchema<T>) as [keyof T, ValidatorSchema<T>[keyof T]][]).forEach(
            ([key, rules]) => {
                const value = formData[key];
                const readableField = String(key)
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, (char) => char.toUpperCase());

                if (!rules) return;

                const isRequired =
                    typeof rules.required === 'function' ? rules.required(formData) : rules.required;

                if (isRequired && (!value || (typeof value === 'string' && !value.trim()))) {
                    newErrors[key] = `${readableField} is required.`;
                    return;
                }

                if (rules.validate && value) {
                    const error = rules.validate(value, formData);
                    if (error) newErrors[key] = error;
                }
            }
        );

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            const firstError = Object.values(newErrors)[0] as string;
            if (firstError) toast.error(firstError);
        }

        return Object.keys(newErrors).length === 0;
    };

    return { formData, setFormData, errors, setErrors, handleChange, validate, getLastParsed: () => lastParsedRef.current };
}
